// Bildverarbeitung und -komprimierung
import { v4 as uuidv4 } from 'uuid';
import loadImage from 'blueimp-load-image';


/**
 * Bild aufnehmen und komprimieren (Mock für MVP)
 * @returns Das aufgenommene und verarbeitete Bild als Data-URL oder null bei Fehler
 */
export const captureImage = async (): Promise<string | null> => {
  try {
    // Kamerazugriff anfordern mit optimierten Einstellungen
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment',
        width: { ideal: 2048 },
        height: { ideal: 2048 }
      }
    });

    // Temporäres Video-Element erstellen
    const video = document.createElement('video');
    video.srcObject = stream;
    video.setAttribute('playsinline', 'true'); // Wichtig für iOS
    
    // Auf Video-Metadaten warten (Größe, etc.)
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => {
        video.play();
        resolve();
      };
    });

    // Einige Frames abwarten für bessere Belichtung und Fokus
    await new Promise(resolve => setTimeout(resolve, 700));

    // Canvas erstellen und Bild aufnehmen
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const context = canvas.getContext('2d', { 
      alpha: false,
      willReadFrequently: true
    });
    
    if (!context) {
      throw new Error('Canvas-Kontext konnte nicht erstellt werden');
    }
    
    // Bild zeichnen
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Stream stoppen
    const tracks = stream.getTracks();
    tracks.forEach(track => track.stop());
    
    // Bild komprimieren, rotieren und zurückgeben
    return await compressImage(canvas.toDataURL('image/jpeg', 0.95));
    
  } catch (error) {
    console.error('Fehler bei der Bildaufnahme:', error);
    return null;
  }
};

/**
 * EXIF-Ausrichtung korrigieren – ohne Komprimierung oder Größenänderung
 * @param dataUrl Das Bild als Data-URL
 * @returns EXIF-korrigiertes Bild als PNG Data-URL (verlustfrei)
 */
export const compressImage = async (dataUrl: string): Promise<string> => {
  try {
    const loadImageResult = await loadImage(dataUrl, {
      canvas: true,
      orientation: true  // Nur EXIF-Rotation, keine Größenänderung
    });

    if (!loadImageResult.image || !(loadImageResult.image instanceof HTMLCanvasElement)) {
      throw new Error('Fehler beim Laden des Bildes');
    }

    const canvas = loadImageResult.image as HTMLCanvasElement;
    return canvas.toDataURL('image/png'); // Verlustfrei
  } catch (error) {
    console.error('Fehler bei der Bildverarbeitung:', error);
    throw new Error('Die Bildverarbeitung ist fehlgeschlagen');
  }
};

/**
 * Größe eines Data-URL-Strings schätzen (in Bytes)
 */
export const getDataUrlSize = (dataUrl: string): number => {
  // Base64-Teil extrahieren und Größe berechnen
  const base64 = dataUrl.split(',')[1];
  return Math.round((base64.length * 3) / 4);
};

/**
 * Hilfsfunktion zum Generieren eines eindeutigen Dateinamens
 */
export const generateUniqueImageName = (): string => {
  return `image-${uuidv4()}.jpg`;
};

/**
 * Extrahiert Metadaten aus einem Bild (EXIF)
 * @param dataUrl Das Bild als Data-URL
 * @returns Promise mit den extrahierten Metadaten
 */
export const extractImageMetadata = async (dataUrl: string): Promise<Record<string, any>> => {
  try {
    const loadImageResult = await loadImage(dataUrl, { meta: true });
    
    if (loadImageResult.data?.exif) {
      return loadImageResult.data.exif.getAll();
    }
    
    return {}; // Keine EXIF-Daten gefunden
  } catch (error) {
    console.error('Fehler beim Extrahieren der Bild-Metadaten:', error);
    return {}; // Fehlerfall
  }
};

/**
 * Wandelt ein File-Objekt in eine Data-URL um
 * @param file Das File-Objekt
 * @returns Promise mit der Data-URL
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      resolve(reader.result as string);
    };
    
    reader.onerror = () => {
      reject(new Error('Fehler beim Lesen der Datei'));
    };
    
    reader.readAsDataURL(file);
  });
};
